import { QuestCompletion, METRICS_EVENTS } from "@cede/types";
import { API_ENDPOINT, QueryKeys } from "@cede/utils";
import { useApi } from "@/hooks/core/useApi";
import { useMetricsEvents } from "@/hooks/data/useMetricsEvents";
import { useTickets, usePollTicketUpdate } from "@/hooks/useTickets";
import { useHubUser } from "@/hooks/user/useHubUser";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useSnackbar } from "notistack";

type ClaimQuestRewardsParams = {
  questCompletion: QuestCompletion;
  exchangeId?: string;
  showSnackbar?: boolean;
};

/**
 * Hook for claiming quest rewards
 * Handles the reward claiming process with optimistic ticket count updates
 */
export const useQuestClaimRewards = () => {
  const { auth } = useHubUser();
  const apiHelper = useApi();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const { trackEvent } = useMetricsEvents();

  // Get current tickets using the unified hook
  const { tickets: currentTicketCount } = useTickets();

  // Get polling function
  const { pollForTicketUpdate } = usePollTicketUpdate();

  const { mutate: claimQuestRewards, isPending: isClaimingRewards } = useMutation<
    unknown,
    Error,
    ClaimQuestRewardsParams
  >({
    mutationFn: async ({ questCompletion, exchangeId }) => {
      const questCompletionId = questCompletion.id;

      // Track when user starts claiming quest rewards
      trackEvent(METRICS_EVENTS.QUEST_CLAIM_REWARDS_START, {
        questCompletionId: questCompletionId,
        questId: questCompletion.questId,
        questName: questCompletion.questName,
        questType: questCompletion.questType,
        questFrequency: questCompletion.questFrequency,
        rewardCount: questCompletion.rewardCount,
        rewardType: questCompletion.rewardType,
        exchangeId,
      });

      // Claim the rewards
      const result = await apiHelper.patchAuth(auth, API_ENDPOINT.QUEST_CLAIM_REWARDS, {
        pathParams: [questCompletionId || "" || ""],
      });

      // Poll for ticket count update to ensure UI reflects changes immediately
      await pollForTicketUpdate(currentTicketCount ?? 0);

      return result;
    },
    onSuccess: (_data, variables) => {
      const { questCompletion, exchangeId, showSnackbar = true } = variables;

      // Invalidate all quest-related queries to refresh the data
      invalidateQuestQueries();

      if (showSnackbar) {
        enqueueSnackbar("Quest rewards claimed successfully!", { variant: "success" });
      }

      // Track successful quest reward claim with quest details
      trackEvent(METRICS_EVENTS.QUEST_CLAIM_REWARDS_SUCCESS, {
        questCompletionId: questCompletion.id,
        questId: questCompletion.questId,
        questName: questCompletion.questName,
        questType: questCompletion.questType,
        questFrequency: questCompletion.questFrequency,
        rewardCount: questCompletion.rewardCount,
        rewardType: questCompletion.rewardType,
        exchangeId,
      });
    },
    onError: (error, variables) => {
      const { questCompletion, exchangeId, showSnackbar = true } = variables;

      if (showSnackbar) {
        enqueueSnackbar(`Failed to claim quest rewards: ${error instanceof Error ? error.message : "Unknown error"}`, {
          variant: "error",
        });
      }

      // Track failed quest reward claim with error details
      trackEvent(METRICS_EVENTS.QUEST_CLAIM_REWARDS_ERROR, {
        questCompletionId: questCompletion.id,
        questId: questCompletion.questId,
        questName: questCompletion.questName,
        questType: questCompletion.questType,
        questFrequency: questCompletion.questFrequency,
        rewardCount: questCompletion.rewardCount,
        rewardType: questCompletion.rewardType,
        exchangeId,
      });
    },
  });

  /**
   * Invalidate all quest-related queries
   */
  const invalidateQuestQueries = () => {
    queryClient.invalidateQueries({ queryKey: [QueryKeys.QUEST_LIVE] });
    queryClient.invalidateQueries({ queryKey: [QueryKeys.QUEST_USER_PROGRESS] });
    queryClient.invalidateQueries({ queryKey: [QueryKeys.QUEST_USER_COMPLETIONS] });
    queryClient.invalidateQueries({ queryKey: [QueryKeys.QUEST_USER_ELIGIBILITY] });

    // Invalidate spinning wheel tickets
    queryClient.invalidateQueries({ queryKey: [QueryKeys.SPINNING_WHEEL_TICKETS] });
  };

  return {
    claimQuestRewards,
    isClaimingRewards,
  };
};
